import { Component } from '@theme/component';

/** From assets/fifi-order-form.js: the design colour or the "Front photo" extra changed. */
const ORDER_CHANGE = 'fifi:order-change';
/** To assets/fifi-order-form.js: a "See it in" dot was picked. */
const DESIGN_COLOUR = 'fifi:design-colour';
/** The stage + thumbnails layout (and hover zoom) starts here; below it the snap gallery. */
const DESKTOP_QUERY = window.matchMedia('(min-width: 990px)');
const HOVER_QUERY = window.matchMedia('(hover: hover)');

/**
 * @typedef {object} Refs
 * @property {HTMLElement} stage
 * @property {HTMLElement} slides - The slide list; a snap scroller below 990px.
 * @property {HTMLElement[]} slideItems
 * @property {HTMLButtonElement[]} [thumbs] - Thumbnails from 990px, label tabs below.
 * @property {HTMLElement} note - "Photos in pink coming soon" / "Shown in pink".
 * @property {HTMLElement} counter - "1 / 3" below 990px.
 * @property {HTMLButtonElement[]} [dots] - "See it in" colour dots.
 * @property {HTMLElement} [colourName]
 */

/**
 * Fifi's V3 product gallery (snippets/fifi-product-gallery.liquid). Always shows the product's own
 * photos; the order form's design colour picks the photo set (alt text "Pink | Back"), falling back
 * to the default set with a "coming soon" note, and the Front photo shows only with its extra.
 *
 * @extends {Component<Refs>}
 */
class FifiProductGallery extends Component {
  requiredRefs = ['stage', 'slides', 'slideItems', 'note', 'counter'];

  #colour = '';
  #pocket = false;

  /** @type {HTMLElement[]} */
  #visible = [];

  /** @type {HTMLElement | undefined} */
  #active;

  #frame = 0;
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const { stage, slides } = this.refs;

    this.closest('.shopify-section')?.addEventListener(ORDER_CHANGE, this.#onOrderChange, { signal });
    stage.addEventListener('pointerenter', this.#onZoomStart, { signal });
    stage.addEventListener('pointermove', this.#onZoomMove, { signal });
    stage.addEventListener('pointerleave', this.#onZoomEnd, { signal });
    slides.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    DESKTOP_QUERY.addEventListener('change', () => this.#show(this.#active, 'instant'), { signal });

    this.#apply();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    cancelAnimationFrame(this.#frame);
  }

  /**
   * A thumbnail or tab.
   * @param {number} index - Index in the slide list.
   */
  select(index) {
    const item = this.refs.slideItems[Number(index)];
    if (item) this.#show(item, 'smooth');
  }

  /**
   * A "See it in" dot: the order form owns the colour, and sends it back with ORDER_CHANGE.
   * @param {Event} event
   */
  pickColour(event) {
    const dot = /** @type {HTMLElement} */ (event.target);
    const colour = dot.dataset.colour ?? '';
    this.#colour = colour;
    this.#apply();
    this.closest('.shopify-section')?.dispatchEvent(new CustomEvent(DESIGN_COLOUR, { detail: { colour } }));
  }

  /** @param {Event} event */
  #onOrderChange = (event) => {
    const detail = /** @type {CustomEvent<{ colour?: string, pocket?: boolean }>} */ (event).detail ?? {};
    const colour = detail.colour ?? '';
    const pocket = Boolean(detail.pocket);
    if (colour === this.#colour && pocket === this.#pocket && this.#visible.length) return;

    const colourChanged = colour !== this.#colour;
    this.#colour = colour;
    this.#pocket = pocket;
    this.#apply(colourChanged);
  };

  /**
   * Shows the photo set for the current colour (or the default set), with the Front photo only when
   * its extra is chosen, and updates the note and the dots.
   * @param {boolean} [restart] - Go back to the first photo.
   */
  #apply(restart = false) {
    const items = this.refs.slideItems;
    const colour = this.#colour.trim().toLowerCase();
    /** @param {HTMLElement} item */
    const isFront = (item) => item.hasAttribute('data-front');

    const hasSet = colour !== '' && items.some((item) => item.dataset.colour === colour && !isFront(item));
    const setColour = hasSet ? colour : '';
    let visible = items.filter((item) => item.dataset.colour === setColour && (this.#pocket || !isFront(item)));

    // A colour set without its own Front photo borrows the default one.
    if (this.#pocket && hasSet && !visible.some(isFront)) {
      visible.push(...items.filter((item) => item.dataset.colour === '' && isFront(item)));
    }
    // Every photo tagged with a colour and none untagged: show them all rather than nothing.
    if (!visible.length) visible = items.filter((item) => this.#pocket || !isFront(item));
    if (!visible.length) visible = [...items];

    this.#visible = visible;
    items.forEach((item, index) => {
      const shown = visible.includes(item);
      item.hidden = !shown;
      const thumb = this.refs.thumbs?.[index];
      if (thumb) thumb.hidden = !shown;
    });
    this.refs.thumbs?.forEach((thumb) => {
      const container = thumb.parentElement;
      if (container) container.hidden = visible.length < 2;
    });

    const { note } = this.refs;
    const template = hasSet ? this.dataset.shownIn : this.dataset.comingSoon;
    note.hidden = colour === '';
    note.textContent = colour === '' ? '' : (template ?? '').replace('__COLOUR__', colour);

    for (const dot of this.refs.dots ?? []) {
      dot.setAttribute('aria-pressed', String((dot.dataset.colour ?? '').toLowerCase() === colour));
    }
    if (this.refs.colourName) this.refs.colourName.textContent = this.#colour || this.dataset.asPictured || '';

    const keep = !restart && this.#active && visible.includes(this.#active) ? this.#active : visible[0];
    this.#show(keep, 'instant');
  }

  /**
   * @param {HTMLElement | undefined} item
   * @param {ScrollBehavior} behavior
   */
  #show(item, behavior) {
    if (!item) return;
    this.#active = item;

    if (!DESKTOP_QUERY.matches) {
      const position = this.#visible.indexOf(item);
      const { slides } = this.refs;
      if (position >= 0) slides.scrollTo({ left: position * slides.clientWidth, behavior });
    }
    this.#markActive();
  }

  #markActive() {
    const items = this.refs.slideItems;
    items.forEach((item, index) => {
      const active = item === this.#active;
      item.toggleAttribute('data-active', active);
      const thumb = this.refs.thumbs?.[index];
      if (thumb) {
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      }
    });

    const position = this.#active ? this.#visible.indexOf(this.#active) : 0;
    this.refs.counter.textContent = `${Math.max(position, 0) + 1} / ${this.#visible.length}`;
  }

  /** Phone: the snap gallery's position sets the counter and the active tab. */
  #onScroll = () => {
    if (DESKTOP_QUERY.matches || this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      const { slides } = this.refs;
      const position = Math.round(slides.scrollLeft / Math.max(slides.clientWidth, 1));
      const item = this.#visible[position];
      if (item && item !== this.#active) {
        this.#active = item;
        this.#markActive();
      }
    });
  };

  #onZoomStart = () => {
    if (!DESKTOP_QUERY.matches || !HOVER_QUERY.matches) return;
    this.refs.stage.toggleAttribute('data-zoom', true);
  };

  /** @param {PointerEvent} event */
  #onZoomMove = (event) => {
    if (!this.refs.stage.hasAttribute('data-zoom')) return;
    const rect = this.refs.stage.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    this.refs.stage.style.setProperty('--fifi-zoom-x', `${Math.round(x)}%`);
    this.refs.stage.style.setProperty('--fifi-zoom-y', `${Math.round(y)}%`);
  };

  #onZoomEnd = () => {
    this.refs.stage.removeAttribute('data-zoom');
  };
}

if (!customElements.get('fifi-product-gallery')) customElements.define('fifi-product-gallery', FifiProductGallery);
